import axios from 'axios';
import { getEndpointCandidates } from '../config/apiConfig.js';

export const AUTH_UNAUTHORIZED_EVENT = 'react-auth:unauthorized';

const DEFAULT_HEADERS = {
    Accept: 'application/json',
};

const inFlightGetRequests = new Map();

function notifyUnauthorized(error) {
    if (typeof window === 'undefined') {
        return;
    }

    if (error?.response?.status !== 401) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent(AUTH_UNAUTHORIZED_EVENT, {
            detail: {
                status: 401,
            },
        }),
    );
}

function stableSerialize(value) {
    if (value === null || value === undefined) {
        return '';
    }

    if (Array.isArray(value)) {
        return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
    }

    if (typeof value === 'object') {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${key}:${stableSerialize(value[key])}`)
            .join(',')}}`;
    }

    return String(value);
}

function buildGetRequestKey({ moduleName, endpoint, pathParams, params, token, dedupeScope }) {
    if (dedupeScope) {
        return dedupeScope;
    }

    return [
        moduleName,
        endpoint,
        stableSerialize(pathParams),
        stableSerialize(params),
        token || '',
    ].join('|');
}

function shouldFallback(error) {
    // 4xx suelen ser errores funcionales (credenciales, validación, permisos), no de infraestructura.
    // No hacer fallback a otro backend ante un 401 o 403.
    if (error?.response?.status && error.response.status < 500) {
        return false;
    }

    return true;
}

let tokenRefreshHandler = null;
let inFlightRefreshPromise = null;

export function registerTokenRefreshHandler(handler) {
    tokenRefreshHandler = handler;
}

export function clearTokenRefreshHandler() {
    tokenRefreshHandler = null;
}

async function executeSilentRefresh() {
    if (inFlightRefreshPromise) {
        return inFlightRefreshPromise;
    }

    if (typeof tokenRefreshHandler !== 'function') {
        return null;
    }

    inFlightRefreshPromise = (async () => {
        try {
            const nextToken = await tokenRefreshHandler();
            return nextToken;
        } finally {
            inFlightRefreshPromise = null;
        }
    })();

    return inFlightRefreshPromise;
}

export function extractApiErrorMessage(error, fallbackMessage = 'Ocurrio un error inesperado.') {
    if (error?.response?.status === 401) {
        return 'Sesión vencida o no autorizada. Iniciá sesión nuevamente.';
    }

    const responseError = error?.response?.data?.error;
    if (error?.response?.status === 409 && responseError === 'version_conflict') {
        return 'La visita cambió mientras la estabas editando. Recargá la visita e intentá finalizar nuevamente.';
    }

    if (responseError) {
        return String(responseError);
    }

    const responseErrors = error?.response?.data?.errors;
    if (responseErrors && typeof responseErrors === 'object') {
        const firstKey = Object.keys(responseErrors)[0];
        const firstValue = firstKey ? responseErrors[firstKey] : null;
        if (Array.isArray(firstValue) && firstValue.length > 0) {
            return String(firstValue[0]);
        }
        if (firstValue) {
            return String(firstValue);
        }
    }

    const responseMessage = error?.response?.data?.message;
    if (responseMessage) {
        return responseMessage;
    }

    if (error?.code === 'ERR_NETWORK') {
        return 'No hay conexion con el backend. Verifica que Laravel/Node esten levantados.';
    }

    if (error?.code === 'ECONNABORTED') {
        return 'El backend demoró demasiado en responder.';
    }

    if (error?.response?.status === 403) {
        return 'No tenes permisos para esta accion.';
    }

    if (error?.response?.status) {
        return `El backend respondió con error HTTP ${error.response.status}.`;
    }

    if (error?.message) {
        return String(error.message);
    }

    return fallbackMessage;
}

export async function requestWithFallback({
    moduleName,
    endpoint,
    endpointIsKey = false,
    pathParams,
    method = 'get',
    data,
    params,
    headers,
    token,
    timeout = 15000,
    dedupeInFlight = true,
    dedupeScope,
    responseType,
    returnFullResponse = false,
    invalidateSessionOnUnauthorized = false,
    _isRetryAfterRefresh = false,
    _isRetryAfterIdempotencyConflict = false,
    axiosInstance = axios,
}) {
    const normalizedMethod = String(method || 'get').toLowerCase();
    const canDedupe = dedupeInFlight && normalizedMethod === 'get';
    const dedupeKey = canDedupe
        ? buildGetRequestKey({ moduleName, endpoint, pathParams, params, token, dedupeScope })
        : null;

    if (canDedupe && inFlightGetRequests.has(dedupeKey)) {
        return inFlightGetRequests.get(dedupeKey);
    }

    const candidates = getEndpointCandidates(moduleName, endpoint, {
        isEndpointKey: endpointIsKey,
        pathParams,
    });

    const requestPromise = (async () => {
        let lastError = null;

        for (let index = 0; index < candidates.length; index += 1) {
            const candidate = candidates[index];

            try {
                const response = await axiosInstance({
                    url: candidate.url,
                    method: normalizedMethod,
                    data,
                    params,
                    timeout,
                    responseType,
                    headers: {
                        ...DEFAULT_HEADERS,
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        ...headers,
                    },
                });

                if (typeof import.meta !== 'undefined' && import.meta.env?.DEV && index > 0) {
                    const firstBackend = candidates[0]?.backend;
                    console.warn(
                        `[api-fallback] ${moduleName}/${endpoint}: ${firstBackend} -> ${candidate.backend}`,
                    );
                }

                return {
                    data: returnFullResponse ? response : response.data,
                    meta: {
                        backend: candidate.backend,
                        url: candidate.url,
                        usedFallback: index > 0,
                    },
                };
            } catch (error) {
                lastError = error;

                if (!shouldFallback(error) || index === candidates.length - 1) {
                    break;
                }
            }
        }

        // Manejo de expiración de token con renovación silenciosa (Silent Refresh)
        if (lastError?.response?.status === 401) {
            const isAuthEndpoint = moduleName === 'auth' && (endpoint === 'refresh' || endpoint === 'login' || endpoint === 'logout');
            const canAttemptRefresh = !_isRetryAfterRefresh && !isAuthEndpoint && typeof tokenRefreshHandler === 'function';

            if (canAttemptRefresh) {
                let refreshedToken = null;
                try {
                    refreshedToken = await executeSilentRefresh();
                } catch (refreshError) {
                    // Si el fallo de refresh fue de red temporal o caída de servidor, no invalidar credenciales
                    const isNetworkOrServerError = !refreshError?.response?.status
                        || refreshError.response.status >= 500
                        || refreshError.code === 'ERR_NETWORK'
                        || refreshError.code === 'ECONNABORTED';

                    if (isNetworkOrServerError) {
                        throw refreshError;
                    }

                    // Si el servidor rechazó definitivamente la renovación (401/403):
                    if (refreshError?.response?.status === 401 || refreshError?.response?.status === 403) {
                        notifyUnauthorized(refreshError);
                    }
                    throw refreshError;
                }

                if (refreshedToken) {
                    // Reintentar la solicitud original con el nuevo token conservando idempotencia
                    return await requestWithFallback({
                        moduleName,
                        endpoint,
                        endpointIsKey,
                        pathParams,
                        method,
                        data,
                        params,
                        headers,
                        token: refreshedToken,
                        timeout,
                        dedupeInFlight: false,
                        responseType,
                        returnFullResponse,
                        invalidateSessionOnUnauthorized,
                        _isRetryAfterRefresh: true,
                        _isRetryAfterIdempotencyConflict,
                        axiosInstance,
                    });
                }
            }

            if (invalidateSessionOnUnauthorized) {
                notifyUnauthorized(lastError);
            }
        }

        // Manejo de conflicto de idempotencia (409 Payload Mismatch):
        // Si el servidor indica que la clave colisionó con un payload anterior, rotamos la clave y reintentamos transparentemente.
        if (lastError?.response?.status === 409 && !_isRetryAfterIdempotencyConflict) {
            const conflict = lastError.response.data;
            const hasIdempotencyHeader = Boolean(headers?.['X-Idempotency-Key'] || headers?.['x-idempotency-key']);

            if (hasIdempotencyHeader && conflict?.code === 'idempotency_payload_mismatch'
                && conflict.operation_status === 'completed'
                && !String(headers?.['X-Idempotency-Key'] || headers?.['x-idempotency-key']).startsWith('operation:')) {
                const rawIdempotencyKey = String(headers?.['X-Idempotency-Key'] || headers?.['x-idempotency-key'] || '');
                const isOpPrefix = rawIdempotencyKey.startsWith('operation:');
                const freshUuid = typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `rot_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
                const rotatedKey = isOpPrefix ? `operation:${freshUuid}` : `rot:${freshUuid}`;

                const rotatedHeaders = {
                    ...headers,
                    'X-Idempotency-Key': rotatedKey,
                };
                if (rotatedHeaders['x-idempotency-key']) {
                    delete rotatedHeaders['x-idempotency-key'];
                }

                let rotatedData = data;
                if (data && typeof data === 'object' && !(data instanceof FormData)) {
                    if (data.client_uuid) {
                        rotatedData = {
                            ...data,
                            client_uuid: freshUuid,
                        };
                    }
                }

                return await requestWithFallback({
                    moduleName,
                    endpoint,
                    endpointIsKey,
                    pathParams,
                    method,
                    data: rotatedData,
                    params,
                    headers: rotatedHeaders,
                    token,
                    timeout,
                    dedupeInFlight: false,
                    responseType,
                    returnFullResponse,
                    invalidateSessionOnUnauthorized,
                    _isRetryAfterRefresh,
                    _isRetryAfterIdempotencyConflict: true,
                    axiosInstance,
                });
            }
        }

        throw lastError;
    })();

    if (canDedupe) {
        inFlightGetRequests.set(dedupeKey, requestPromise);
    }

    try {
        return await requestPromise;
    } finally {
        if (canDedupe) {
            inFlightGetRequests.delete(dedupeKey);
        }
    }
}

